import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { captureUtm, bindClickTracking } from "@/lib/track";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import MarketingLayout from "@/components/MarketingLayout";
import PortalLayout from "@/components/PortalLayout";

import Home from "@/pages/Home";
import TenK30 from "@/pages/TenK30";
import Results from "@/pages/Results";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Join from "@/pages/Join";
import PaymentSuccess from "@/pages/PaymentSuccess";
import SetPassword from "@/pages/SetPassword";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";

import Dashboard from "@/pages/portal/Dashboard";
import WeekDetail from "@/pages/portal/WeekDetail";
import Lesson from "@/pages/portal/Lesson";
import FoundationReview from "@/pages/portal/FoundationReview";
import Train from "@/pages/portal/Train";
import Move from "@/pages/portal/Move";
import Eat from "@/pages/portal/Eat";
import EatMeals from "@/pages/portal/EatMeals";
import EatMealDetail from "@/pages/portal/EatMealDetail";
import EatProduce from "@/pages/portal/EatProduce";
import EatShopping from "@/pages/portal/EatShopping";
import EatPrep from "@/pages/portal/EatPrep";
import EatPrinciples from "@/pages/portal/EatPrinciples";
import AdminMeals from "@/pages/portal/AdminMeals";
import AdminSiteVideos from "@/pages/portal/AdminSiteVideos";
import AdminLessons from "@/pages/portal/AdminLessons";
import Learn from "@/pages/portal/Learn";
import Track from "@/pages/portal/Track";
import CommunityPortal from "@/pages/portal/CommunityPortal";
import LeanDashboard from "@/pages/portal/LeanDashboard";
import LeanStart from "@/pages/portal/LeanStart";
import LeanWeek from "@/pages/portal/LeanWeek";
import BuildDashboard from "@/pages/portal/BuildDashboard";
import BuildStart from "@/pages/portal/BuildStart";
import BuildWeek from "@/pages/portal/BuildWeek";
import Ask from "@/pages/portal/Ask";
import Profile from "@/pages/portal/Profile";
import Admin from "@/pages/portal/Admin";
import Recover from "@/pages/portal/Recover";

import "@/App.css";

const M = (P) => <MarketingLayout><P /></MarketingLayout>;
const wrap = (Comp) => <MarketingLayout><Comp /></MarketingLayout>;

function AppShell({ children }) {
  return (
    <ProtectedRoute>
      <PortalLayout>{children}</PortalLayout>
    </ProtectedRoute>
  );
}

export default function App() {
  useEffect(() => { captureUtm(); return bindClickTracking(); }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={wrap(Home)} />
            <Route path="/10k30" element={wrap(TenK30)} />
            <Route path="/results" element={wrap(Results)} />
            <Route path="/blog" element={wrap(Blog)} />
            <Route path="/blog/:slug" element={wrap(BlogPost)} />
            <Route path="/about" element={wrap(About)} />
            <Route path="/privacy" element={wrap(Privacy)} />
            <Route path="/terms" element={wrap(Terms)} />
            <Route path="/join" element={wrap(Join)} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={wrap(Join)} />
            <Route path="/set-password/:token" element={<SetPassword />} />
            <Route path="/login" element={wrap(Login)} />
            <Route path="/signup" element={wrap(Signup)} />

            <Route path="/onboarding" element={
              <ProtectedRoute requireOnboarded={false}><Onboarding /></ProtectedRoute>
            } />

            <Route path="/app" element={<AppShell><Dashboard /></AppShell>} />
            <Route path="/app/week/:n" element={<AppShell><WeekDetail /></AppShell>} />
            <Route path="/app/lesson/:n" element={<AppShell><Lesson /></AppShell>} />
            <Route path="/app/review/:stage" element={<AppShell><FoundationReview /></AppShell>} />
            <Route path="/app/train" element={<AppShell><Train /></AppShell>} />
            <Route path="/app/move" element={<AppShell><Move /></AppShell>} />
            <Route path="/app/eat" element={<AppShell><Eat /></AppShell>} />
            <Route path="/app/eat/meals" element={<AppShell><EatMeals /></AppShell>} />
            <Route path="/app/eat/meals/:id" element={<AppShell><EatMealDetail /></AppShell>} />
            <Route path="/app/eat/produce" element={<AppShell><EatProduce /></AppShell>} />
            <Route path="/app/eat/shopping" element={<AppShell><EatShopping /></AppShell>} />
            <Route path="/app/eat/prep" element={<AppShell><EatPrep /></AppShell>} />
            <Route path="/app/eat/principles" element={<AppShell><EatPrinciples /></AppShell>} />
            <Route path="/app/admin/meals" element={
              <ProtectedRoute admin><PortalLayout><AdminMeals /></PortalLayout></ProtectedRoute>
            } />
            <Route path="/app/admin/lessons" element={
              <ProtectedRoute admin><PortalLayout><AdminLessons /></PortalLayout></ProtectedRoute>
            } />
            <Route path="/app/admin/site-videos" element={
              <ProtectedRoute admin><PortalLayout><AdminSiteVideos /></PortalLayout></ProtectedRoute>
            } />
            <Route path="/app/recover" element={<AppShell><Recover /></AppShell>} />
            <Route path="/app/learn" element={<AppShell><Learn /></AppShell>} />
            <Route path="/app/track" element={<AppShell><Track /></AppShell>} />
            <Route path="/app/community" element={<AppShell><CommunityPortal /></AppShell>} />
            <Route path="/app/lean" element={<AppShell><LeanDashboard /></AppShell>} />
            <Route path="/app/lean/start" element={<AppShell><LeanStart /></AppShell>} />
            <Route path="/app/lean/week/:n" element={<AppShell><LeanWeek /></AppShell>} />
            <Route path="/app/build" element={<AppShell><BuildDashboard /></AppShell>} />
            <Route path="/app/build/start" element={<AppShell><BuildStart /></AppShell>} />
            <Route path="/app/build/week/:n" element={<AppShell><BuildWeek /></AppShell>} />
            <Route path="/app/ask" element={<AppShell><Ask /></AppShell>} />
            <Route path="/app/profile" element={<AppShell><Profile /></AppShell>} />
            <Route path="/app/admin" element={
              <ProtectedRoute admin><PortalLayout><Admin /></PortalLayout></ProtectedRoute>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
